/**
 * Tiny IndexedDB key/value store (raw indexedDB API, no deps) for drafts that
 * must survive an auth detour — including Blobs and large data URLs that don't
 * fit comfortably in localStorage.
 *
 * Every function resolves (never rejects) so callers can treat persistence as
 * best-effort: on failure, reads return null and writes return false.
 */

const DB_NAME = 'lovingecards-drafts';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';

interface DraftRecord<T> {
  value: T;
  savedAt: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (typeof window === 'undefined' || !('indexedDB' in window)) {
    return Promise.resolve(null);
  }
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  }).then((db) => {
    // Allow a retry later if opening failed (e.g. private mode)
    if (!db) dbPromise = null;
    return db;
  });

  return dbPromise;
}

function runRequest<R>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<R>
): Promise<R | null> {
  return openDb().then(
    (db) =>
      new Promise<R | null>((resolve) => {
        if (!db) return resolve(null);
        try {
          const tx = db.transaction(STORE_NAME, mode);
          const request = fn(tx.objectStore(STORE_NAME));
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => resolve(null);
          tx.onabort = () => resolve(null);
        } catch {
          resolve(null);
        }
      })
  );
}

/** Save a value (anything structured-cloneable, incl. Blob) under `key`. */
export async function saveDraft<T>(key: string, value: T): Promise<boolean> {
  const record: DraftRecord<T> = { value, savedAt: Date.now() };
  const result = await runRequest('readwrite', (store) => store.put(record, key));
  return result !== null;
}

/**
 * Load a value saved with `saveDraft`. Drafts older than `maxAgeMs`
 * (default 7 days) are discarded and null is returned.
 */
export async function loadDraft<T>(key: string, maxAgeMs = 7 * 24 * 60 * 60 * 1000): Promise<T | null> {
  const record = await runRequest<DraftRecord<T> | undefined>('readonly', (store) => store.get(key));
  if (!record || typeof record !== 'object' || !('value' in record)) return null;
  if (!record.savedAt || Date.now() - record.savedAt > maxAgeMs) {
    void deleteDraft(key);
    return null;
  }
  return record.value;
}

export async function deleteDraft(key: string): Promise<void> {
  await runRequest('readwrite', (store) => store.delete(key));
}
